<script setup>
import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import countries from 'shared/constants/countries';
import { dynamicTime } from 'shared/helpers/timeHelper';

import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'next/polaris/ui/item';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    default: 0,
  },
  email: {
    type: String,
    default: '',
  },
  phone: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  thumbnail: {
    type: String,
    default: '',
  },
  accountId: {
    type: [String, Number],
    default: 0,
  },
  additionalAttributes: {
    type: Object,
    default: () => ({}),
  },
  updatedAt: {
    type: Number,
    default: 0,
  },
});

const navigateTo = computed(() => {
  return frontendURL(`accounts/${props.accountId}/contacts/${props.id}`);
});

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const updatedAtTime = computed(() => {
  if (!props.updatedAt) return '';
  return dynamicTime(props.updatedAt);
});

const countryDetails = computed(() => {
  const { country, countryCode, city } = props.additionalAttributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});
</script>

<template>
  <Item
    as-child
    size="sm"
    class="w-full cursor-pointer flex-nowrap items-start px-3 text-start text-polaris-foreground hover:bg-polaris-accent"
  >
    <!-- Compact contact row: avatar, name, e-mail, phone (the kit's contact card, without its frame); the whole row is the link. -->
    <router-link :to="navigateTo">
      <ItemMedia>
        <Avatar :name="name" :src="thumbnail" :size="32" rounded-full />
      </ItemMedia>
      <ItemContent class="min-w-0 gap-0.5">
        <ItemTitle class="w-full justify-between font-normal">
          <span class="truncate">{{ name }}</span>
          <span
            v-if="updatedAtTime"
            class="shrink-0 text-xs font-normal text-polaris-muted-foreground"
          >
            {{ $t('SEARCH.UPDATED_AT', { time: updatedAtTime }) }}
          </span>
        </ItemTitle>
        <ItemDescription
          v-if="email"
          class="mb-0 line-clamp-none flex min-w-0 items-center gap-1.5 text-xs"
        >
          <Icon icon="i-lucide-mail" class="size-3.5 shrink-0" />
          <span class="min-w-0 truncate" :title="email">{{ email }}</span>
        </ItemDescription>
        <ItemDescription
          v-if="phone"
          class="mb-0 line-clamp-none flex min-w-0 items-center gap-1.5 text-xs"
        >
          <Icon icon="i-lucide-phone" class="size-3.5 shrink-0" />
          <span class="min-w-0 truncate" :title="phone">{{ phone }}</span>
        </ItemDescription>
        <p
          v-if="countryDetails"
          class="mb-0 flex min-w-0 items-center gap-1.5 text-xs text-polaris-muted-foreground"
        >
          <Flag :country="countryDetails.countryCode" class="size-3 shrink-0" />
          <span class="min-w-0 truncate">{{ formattedLocation }}</span>
        </p>
      </ItemContent>
    </router-link>
  </Item>
</template>
